import { Controller } from "@hotwired/stimulus";

/**
 * Narrows a rendered list to the rows that match what is typed.
 *
 * Client-side on purpose. The surfaces that use it — the documents of a lot, and lists of
 * the same shape — are already loaded as one HTMX fragment, so a query string would
 * round-trip the whole panel to hide four rows, and would lose the panel's scroll position
 * on the way back.
 *
 * Rows carry what they match on in `data-list-filter-text`, written once by the server and
 * already lowercased. Reading the cells instead would match on formatting — a date shown
 * as 03/09/2026 does not contain "septembre", and a truncated title does not contain its
 * own end.
 *
 * Group headers keep a live count and hide themselves when nothing under them survives,
 * because a section header over an empty table reads as a loading failure.
 */
export default class extends Controller<HTMLElement> {
    static targets = ["empty", "count"];

    declare readonly hasEmptyTarget: boolean;
    declare readonly emptyTarget: HTMLElement;

    apply(event: Event): void {
        const input = event.target;
        const needle = input instanceof HTMLInputElement ? input.value.trim().toLowerCase() : "";

        let shown = 0;

        this.element.querySelectorAll<HTMLElement>("[data-list-filter-group]").forEach((group) => {
            const rows = Array.from(group.querySelectorAll<HTMLElement>("[data-list-filter-text]"));
            let visibleInGroup = 0;

            rows.forEach((row) => {
                const matches = needle === "" || (row.dataset.listFilterText ?? "").includes(needle);
                row.hidden = !matches;
                if (matches) visibleInGroup += 1;
            });

            group.hidden = visibleInGroup === 0;
            const count = group.querySelector<HTMLElement>("[data-list-filter-target='count']");
            if (count) count.textContent = String(visibleInGroup);
            shown += visibleInGroup;
        });

        if (this.hasEmptyTarget) this.emptyTarget.hidden = shown > 0;
    }
}
